/**
 * portal-link - the member's page on the main portal, as a LINK SHAPE (row 400, approval 193;
 * William, 19 Sep 2026: "Name (link to the member's page on the main portal)").
 *
 * MIRROR of api/src/lib/portal-link.ts, pinned by tests/unit/portal-link.test.ts: the API validates
 * the setting `MEMBERCARE_PORTAL_MEMBER_LINK` before it leaves the server (features.portalMemberLink on
 * the sign-in read), and the screen validates it AGAIN here before it builds a URL. Absent or invalid,
 * the name is plain text. Only `{campaign}` and `{member}` substitute, values URL-encoded, and the
 * template must start with https://.
 */

export const PORTAL_LINK_PLACEHOLDERS = ['campaign', 'member'] as const;
export type PortalLinkPlaceholder = (typeof PORTAL_LINK_PLACEHOLDERS)[number];

const MAX_TEMPLATE = 2048;
const BRACES = /\{([^{}]*)\}/g;
const HOST = /^https:\/\/[A-Za-z0-9.-]+(?::\d{1,5})?(?:[/?#]|$)/;

/** The template when it is safe to build from; '' otherwise. */
export function validPortalLinkTemplate(raw: unknown): string {
  if (typeof raw !== 'string') return '';
  const t = raw.trim();
  if (!t || t.length > MAX_TEMPLATE) return '';
  // https only, and the HOST is literal text, never a placeholder: `https://{member}.example.com` is refused.
  if (!HOST.test(t)) return '';
  if (/[\s\u0000-\u001f\u007f]/.test(t)) return '';
  // Every brace must belong to a known placeholder: strip those, and no brace may remain.
  const names = [...t.matchAll(BRACES)].map((m) => m[1] ?? '');
  if (names.some((n) => !(PORTAL_LINK_PLACEHOLDERS as readonly string[]).includes(n))) return '';
  if (t.replace(BRACES, '').includes('{') || t.replace(BRACES, '').includes('}')) return '';
  if (!names.includes('member')) return '';
  // It must parse as a URL once the placeholders carry a value (the scheme was settled by HOST above).
  try {
    const probe = new URL(t.replace(BRACES, 'x'));
    if (!probe.hostname) return '';
  } catch {
    return '';
  }
  return t;
}

/**
 * The URL for one member, or null: the template must be valid, and every placeholder it names must
 * have a non-empty value (a row with no campaign gets no link rather than a link to nowhere).
 */
export function portalMemberUrl(template: unknown, values: { campaign?: string | null | undefined; member?: string | null | undefined }): string | null {
  const t = validPortalLinkTemplate(template);
  if (!t) return null;
  const v: Record<PortalLinkPlaceholder, string> = {
    campaign: String(values.campaign ?? '').trim(),
    member: String(values.member ?? '').trim(),
  };
  for (const name of [...t.matchAll(BRACES)].map((m) => m[1] as PortalLinkPlaceholder)) {
    if (!v[name]) return null;
  }
  return t.replace(BRACES, (_m, name: PortalLinkPlaceholder) => encodeURIComponent(v[name]));
}
